import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Draw-with-your-finger signature capture.
 *
 * Pointer events cover mouse, finger and stylus in one code path. The canvas is
 * sized to its container in device pixels so a signature drawn on a phone is not a
 * blurry mess, and the stroke is smoothed with a midpoint curve so a slow finger
 * does not produce a chain of visible straight segments.
 *
 * `onChange` receives a PNG Blob, or null when the pad is cleared.
 */
const SignaturePad = ({ onChange, height = 180, label = 'Sign here' }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPoint = useRef(null);
  const [hasInk, setHasInk] = useState(false);

  // Redraws lose the strokes, so the canvas is only resized when it is empty.
  const fitToContainer = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || hasInk) return;

    const ratio = window.devicePixelRatio || 1;
    const width = canvas.parentElement.clientWidth;

    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const context = canvas.getContext('2d');
    context.scale(ratio, ratio);
    context.lineWidth = 2.2;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.strokeStyle = '#16181a';
  }, [height, hasInk]);

  useEffect(() => {
    fitToContainer();
    window.addEventListener('resize', fitToContainer);
    return () => window.removeEventListener('resize', fitToContainer);
  }, [fitToContainer]);

  const pointFrom = (event) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const emit = () => {
    if (!onChange) return;
    canvasRef.current.toBlob((blob) => onChange(blob), 'image/png');
  };

  const start = (event) => {
    event.preventDefault();
    canvasRef.current.setPointerCapture(event.pointerId);
    drawing.current = true;
    lastPoint.current = pointFrom(event);
    setHasInk(true);

    // A single tap should still leave a dot.
    const context = canvasRef.current.getContext('2d');
    context.beginPath();
    context.arc(lastPoint.current.x, lastPoint.current.y, 1.1, 0, Math.PI * 2);
    context.fillStyle = '#16181a';
    context.fill();
  };

  const move = (event) => {
    if (!drawing.current) return;
    event.preventDefault();

    const point = pointFrom(event);
    const previous = lastPoint.current;
    const context = canvasRef.current.getContext('2d');

    // Curve through the midpoint so the line reads as handwriting.
    const midX = (previous.x + point.x) / 2;
    const midY = (previous.y + point.y) / 2;

    context.beginPath();
    context.moveTo(previous.x, previous.y);
    context.quadraticCurveTo(previous.x, previous.y, midX, midY);
    context.stroke();

    lastPoint.current = point;
  };

  const end = (event) => {
    if (!drawing.current) return;
    drawing.current = false;
    lastPoint.current = null;
    canvasRef.current.releasePointerCapture?.(event.pointerId);
    emit();
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    context.save();
    context.setTransform(1, 0, 0, 1, 0, 0);
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.restore();
    setHasInk(false);
    onChange?.(null);
  };

  return (
    <div className="signature-pad">
      <div className="signature-canvas-wrap" style={{ height }}>
        <canvas
          ref={canvasRef}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerLeave={end}
          onPointerCancel={end}
          // Stops a finger-drag from scrolling the page instead of drawing.
          style={{ touchAction: 'none' }}
          aria-label={label}
          role="img"
        />
        {!hasInk ? <span className="signature-hint">{label}</span> : null}
        <span className="signature-baseline" aria-hidden="true" />
      </div>

      <div className="signature-actions">
        <button type="button" className="link-btn" onClick={clear} disabled={!hasInk}>
          Clear and start again
        </button>
        <span className="field-hint">Use your finger on a phone, or the mouse on a computer.</span>
      </div>
    </div>
  );
};

export default SignaturePad;
